import { useMemo } from 'react';
import type { WatchBrand } from '../../types';
import type { CreateBrandSeed, EditBrandPatchSeed } from '../CuratedWatches/brandSeeds';
import { Button } from '../common/Button';
import { MA_SOURCE_LABEL } from './fieldSpec';
import { buildWkMatchIndex } from './matching';
import { ProposalDetail } from './ProposalDetail';
import { ProposalsList } from './ProposalsList';
import { resolveProposalMatch, type ProposalMatch } from './proposalMatch';
import { clearReviewDrafts, patchProposalsUi, useSessionCache } from './sessionCache';
import { setSessionStatus, useSessionStatuses } from './sessionStatus';
import {
  refreshMaBrands,
  refreshWkBrand,
  retryMaList,
  retryWkBrands,
  useMaBrands,
} from './useMaBrands';

interface ProposalsPageProps {
  /** Hand a Create seed to BrandEditor (the dashboard switches tab to show it). */
  onCreateBrand: (seed: CreateBrandSeed) => void;
  /** Hand an Update seed to BrandEditor. */
  onUpdateBrand: (seed: EditBrandPatchSeed) => void;
}

/**
 * Record that BrandEditor finished a seeded Create or Update, so the row shows
 * it for the rest of the session. Called by the dashboard from BrandEditor's
 * `…Applied` callbacks — at which point this tab is not mounted, which is why
 * this is a plain function over the session stores rather than a prop.
 */
export function recordProposalApplied(
  kind: 'created' | 'updated',
  brandId: string,
  seed: CreateBrandSeed | EditBrandPatchSeed,
) {
  const slug = seed.externalRefs.microbrandAtlas?.slug;
  if (!slug) return;
  setSessionStatus(slug, { kind, wkBrandId: brandId });
  clearReviewDrafts(slug);
  void refreshWkBrand(brandId);
}

/**
 * Catalogue → External Brand Proposals. Lists the Microbrand Atlas catalogue,
 * pairs each brand with a WK brand where it can, and lets a reviewer push
 * chosen fields into BrandEditor. Session-only: nothing is stored until the
 * reviewer saves there.
 */
export function ProposalsPage({ onCreateBrand, onUpdateBrand }: ProposalsPageProps) {
  const {
    listStatus,
    listError,
    listErrorIsConfig,
    rows,
    prefetchError,
    listFetchedAt,
    wkStatus,
    wkError,
    wkBrands,
  } = useMaBrands();
  const ui = useSessionCache((s) => s.ui);
  const { statuses, rejectedMatches } = useSessionStatuses();

  const matches = useMemo(() => {
    const index = buildWkMatchIndex(wkBrands);
    const wkById = new Map<string, WatchBrand>(wkBrands.map((brand) => [brand.id, brand]));
    const result = new Map<string, ProposalMatch>();
    for (const row of rows) {
      result.set(
        row.slug,
        resolveProposalMatch({
          ma: row,
          index,
          wkById,
          status: statuses.get(row.slug),
          autoMatchRejected: rejectedMatches.has(row.slug),
        }),
      );
    }
    return result;
  }, [rows, wkBrands, statuses, rejectedMatches]);

  const selectedRow =
    ui.view === 'detail' && ui.selectedSlug
      ? rows.find((row) => row.slug === ui.selectedSlug)
      : undefined;
  const selectedMatch = selectedRow ? matches.get(selectedRow.slug) : undefined;

  const loading =
    listStatus === 'idle' || listStatus === 'loading' || wkStatus === 'idle' || wkStatus === 'loading';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">External Brand Proposals</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            Brands listed in {MA_SOURCE_LABEL}, compared with the WK catalogue. Open one to choose
            which fields to bring across; nothing is written until you save in the brand editor.
            The list is held for this browser session only.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {listFetchedAt && (
            <span className="text-xs text-gray-500">
              Fetched {new Date(listFetchedAt).toLocaleTimeString()}
            </span>
          )}
          <Button variant="secondary" onClick={refreshMaBrands} loading={loading} disabled={listErrorIsConfig}>
            Refresh
          </Button>
        </div>
      </div>

      {listStatus === 'error' && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-red-300 bg-red-50 p-4 text-red-900"
        >
          <div className="min-w-0 flex-1">
            <div className="font-semibold">
              {listErrorIsConfig
                ? `${MA_SOURCE_LABEL} is not configured`
                : `Could not load the ${MA_SOURCE_LABEL} catalogue`}
            </div>
            <div className="break-words text-sm">{listError}</div>
          </div>
          {!listErrorIsConfig && <Button onClick={retryMaList}>Retry</Button>}
        </div>
      )}

      {wkStatus === 'error' && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-red-300 bg-red-50 p-4 text-red-900"
        >
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Could not load WK brands</div>
            <div className="break-words text-sm">
              {wkError} — without them nothing can be matched, so the list is withheld.
            </div>
          </div>
          <Button onClick={retryWkBrands}>Retry</Button>
        </div>
      )}

      {loading && listStatus !== 'error' && wkStatus !== 'error' && (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Loading the {MA_SOURCE_LABEL} catalogue and WK brands…
        </div>
      )}

      {listStatus === 'ready' && wkStatus === 'ready' && (
        <>
          {prefetchError && (
            <div
              role="alert"
              className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            >
              <div className="min-w-0 flex-1">
                The platform and founder columns could not be loaded ({prefetchError}), so those two
                filters are off. Everything else works.
              </div>
              <Button variant="secondary" size="sm" onClick={retryMaList}>
                Retry
              </Button>
            </div>
          )}

          {selectedRow && selectedMatch ? (
            <ProposalDetail
              row={selectedRow}
              match={selectedMatch}
              wkBrands={wkBrands}
              onBack={() => patchProposalsUi({ view: 'list' })}
              onCreate={onCreateBrand}
              onUpdate={onUpdateBrand}
            />
          ) : (
            <ProposalsList
              rows={rows}
              matches={matches}
              filtersUnavailable={Boolean(prefetchError)}
              onOpen={(slug) => {
                patchProposalsUi({ view: 'detail', selectedSlug: slug });
                window.scrollTo(0, 0);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
